import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, Eye, Building2, PlaneTakeoff, Wallet, CreditCard, DollarSign, TrendingUp, ShieldCheck, ShieldAlert } from "lucide-react";

const METHOD_LABELS = {
  stripe: { label: "Stripe", color: "bg-blue-50 border-blue-100", textColor: "text-blue-700", badgeColor: "text-blue-600" },
  cash: { label: "Cash", color: "bg-green-50 border-green-100", textColor: "text-green-700", badgeColor: "text-green-600" },
  zelle: { label: "Zelle", color: "bg-purple-50 border-purple-100", textColor: "text-purple-700", badgeColor: "text-purple-600" },
  manual: { label: "Manual", color: "bg-gray-50 border-gray-100", textColor: "text-gray-700", badgeColor: "text-gray-600" },
  paypal: { label: "PayPal", color: "bg-yellow-50 border-yellow-100", textColor: "text-yellow-700", badgeColor: "text-yellow-600" },
  cashapp: { label: "CashApp", color: "bg-emerald-50 border-emerald-100", textColor: "text-emerald-700", badgeColor: "text-emerald-600" },
  bank_transfer: { label: "Bank Transfer", color: "bg-indigo-50 border-indigo-100", textColor: "text-indigo-700", badgeColor: "text-indigo-600" },
  apple_pay: { label: "Apple Pay", color: "bg-slate-50 border-slate-100", textColor: "text-slate-700", badgeColor: "text-slate-600" },
  unknown: { label: "Other", color: "bg-slate-50 border-slate-100", textColor: "text-slate-700", badgeColor: "text-slate-600" }
};

function getMethodStyle(method) {
  return METHOD_LABELS[method] || METHOD_LABELS.unknown;
}

export function StatsBreakdownCards({ stats }) {
  const totalCollected = stats?.total_collected || 0;
  const totalTripValue = stats?.total_trip_value || 0;
  const totalBalance = stats?.total_balance_remaining || 0;
  const totalDeposits = stats?.total_deposits_collected || 0;

  return (
    <>
      {/* App Value Summary */}
      <Card className="border-0 shadow-lg mt-4 bg-gradient-to-r from-emerald-600 to-green-700 text-white" data-testid="app-value-card">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5" />
            <p className="font-bold text-base md:text-lg">Business Value Overview</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white/15 rounded-xl p-3" data-testid="value-total-trip">
              <p className="text-xs text-white/70">Total Trip Value</p>
              <p className="text-xl md:text-2xl font-extrabold">${totalTripValue.toFixed(2)}</p>
              <p className="text-[10px] text-white/60">All bookings combined</p>
            </div>
            <div className="bg-white/15 rounded-xl p-3" data-testid="value-total-collected">
              <p className="text-xs text-white/70">Total Collected</p>
              <p className="text-xl md:text-2xl font-extrabold">${totalCollected.toFixed(2)}</p>
              <p className="text-[10px] text-white/60">All payments received</p>
            </div>
            <div className="bg-white/15 rounded-xl p-3" data-testid="value-total-deposits">
              <p className="text-xs text-white/70">Total Deposits</p>
              <p className="text-xl md:text-2xl font-extrabold">${totalDeposits.toFixed(2)}</p>
              <p className="text-[10px] text-white/60">Upfront deposits</p>
            </div>
            <div className="bg-white/15 rounded-xl p-3" data-testid="value-balance-remaining">
              <p className="text-xs text-white/70">Outstanding Balance</p>
              <p className="text-xl md:text-2xl font-extrabold text-yellow-300">${totalBalance.toFixed(2)}</p>
              <p className="text-[10px] text-white/60">Still to collect</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deposit Verification Status */}
      {stats?.deposit_verification && (
        <Card className="border-0 shadow-md mt-4" data-testid="deposit-verification-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-green-600" />
              <p className="font-bold text-sm">Deposit Verification</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-green-50 rounded-xl p-3 border border-green-100" data-testid="verified-deposits-count">
                <div className="flex items-center gap-1.5 mb-1">
                  <ShieldCheck className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-700 font-semibold">Verified</span>
                </div>
                <p className="text-xl font-bold text-green-800">{stats.deposit_verification.verified}</p>
                <p className="text-[10px] text-green-600">${(stats.deposit_verification.verified_amount || 0).toFixed(2)}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-testid="unverified-deposits-count">
                <div className="flex items-center gap-1.5 mb-1">
                  <ShieldAlert className="w-4 h-4 text-amber-600" />
                  <span className="text-xs text-amber-700 font-semibold">Unverified</span>
                </div>
                <p className="text-xl font-bold text-amber-800">{stats.deposit_verification.unverified}</p>
                <p className="text-[10px] text-amber-600">${(stats.deposit_verification.unverified_amount || 0).toFixed(2)}</p>
              </div>
              {Object.entries(stats.deposit_verification.verified_by_method || {}).map(([method, count]) => {
                const style = getMethodStyle(method);
                return (
                  <div key={method} className={`${style.color} rounded-xl p-3 border`}>
                    <span className={`text-xs font-semibold ${style.badgeColor}`}>{style.label}</span>
                    <p className={`text-xl font-bold ${style.textColor}`}>{count}</p>
                    <p className={`text-[10px] ${style.badgeColor}`}>verified</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deposits by Payment Method */}
      {stats?.deposits_by_method && Object.keys(stats.deposits_by_method).length > 0 && (
        <Card className="border-0 shadow-md mt-4" data-testid="deposits-by-method-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-5 h-5 text-emerald-600" />
              <p className="font-bold text-sm">Deposits Collected by Method</p>
              <span className="text-xs text-muted-foreground ml-auto">
                Total: ${(stats.total_deposits_collected || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.deposits_by_method).sort((a, b) => b[1] - a[1]).map(([method, amount]) => {
                const style = getMethodStyle(method);
                return (
                  <div
                    key={method}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${style.color} border`}
                    data-testid={`deposit-method-${method}`}
                  >
                    <span className={`text-sm font-semibold ${style.textColor}`}>${amount.toFixed(2)}</span>
                    <span className={`text-xs ${style.badgeColor}`}>{style.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments by Method */}
      <Card className="border-0 shadow-md mt-4" data-testid="payments-by-method-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-blue-600" />
            <p className="font-bold text-sm">Total Collected by Method</p>
            <span className="text-xs text-muted-foreground ml-auto">
              Total: ${(stats?.total_collected || 0).toFixed(2)}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {['stripe', 'cash', 'zelle', 'cashapp', 'bank_transfer', 'manual'].map((method) => {
              const amount = (stats?.payments_by_method || {})[method] || 0;
              const style = getMethodStyle(method);
              return (
                <div
                  key={method}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${style.color} border ${amount === 0 ? 'opacity-50' : ''}`}
                  data-testid={`payment-method-${method}`}
                >
                  <span className={`text-sm font-semibold ${style.textColor}`}>${amount.toFixed(2)}</span>
                  <span className={`text-xs ${style.badgeColor}`}>{style.label}</span>
                </div>
              );
            })}
            {/* Show any extra methods not in the standard list */}
            {Object.entries(stats?.payments_by_method || {}).filter(([m]) => !['stripe', 'cash', 'zelle', 'cashapp', 'bank_transfer', 'manual'].includes(m)).sort((a, b) => b[1] - a[1]).map(([method, amount]) => {
              const style = getMethodStyle(method);
              return (
                <div
                  key={method}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${style.color} border`}
                  data-testid={`payment-method-${method}`}
                >
                  <span className={`text-sm font-semibold ${style.textColor}`}>${amount.toFixed(2)}</span>
                  <span className={`text-xs ${style.badgeColor}`}>{style.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {stats?.housing_counts && Object.keys(stats.housing_counts).length > 0 && (
        <Card className="border-0 shadow-md mt-4" data-testid="housing-counts-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-5 h-5 text-emerald-600" />
              <p className="font-bold text-sm">Total Pickup by Housing</p>
              <span className="text-xs text-muted-foreground ml-auto">
                {Object.values(stats.housing_counts).reduce((a, b) => a + b, 0)} passengers
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.housing_counts).map(([housing, count]) => (
                <div
                  key={housing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100"
                  data-testid={`housing-${housing}`}
                >
                  <span className="text-sm font-semibold text-emerald-700">{count}</span>
                  <span className="text-xs text-emerald-600">{housing}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.return_housing_counts && Object.keys(stats.return_housing_counts).length > 0 && (
        <Card className="border-0 shadow-md mt-4" data-testid="return-housing-counts-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <PlaneTakeoff className="w-5 h-5 text-orange-600" />
              <p className="font-bold text-sm">Return Pickup by Housing</p>
              <span className="text-xs text-muted-foreground ml-auto">
                {Object.values(stats.return_housing_counts).reduce((a, b) => a + b, 0)} passengers
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.return_housing_counts).map(([housing, count]) => (
                <div
                  key={housing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100"
                  data-testid={`return-housing-${housing}`}
                >
                  <span className="text-sm font-semibold text-orange-700">{count}</span>
                  <span className="text-xs text-orange-600">{housing}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.country_counts && Object.keys(stats.country_counts).length > 0 && (
        <Card className="border-0 shadow-md mt-4" data-testid="country-counts-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-5 h-5 text-indigo-600" />
              <p className="font-bold text-sm">Passengers by Country of Origin</p>
              <span className="text-xs text-muted-foreground ml-auto">
                {Object.values(stats.country_counts).reduce((a, b) => a + b, 0)} total
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.country_counts).map(([country, count]) => (
                <div
                  key={country}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100"
                  data-testid={`country-${country}`}
                >
                  <span className="text-sm font-semibold text-indigo-700">{count}</span>
                  <span className="text-xs text-indigo-600">{country}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.page_visit_counts && Object.keys(stats.page_visit_counts).length > 0 && (
        <Card className="border-0 shadow-md mt-4" data-testid="page-visits-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-5 h-5 text-indigo-600" />
              <p className="font-bold text-sm">Page Visits</p>
              <span className="text-xs text-muted-foreground ml-auto">
                {stats.total_page_visits} views / {stats.total_unique_visitors} unique
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.page_visit_counts).map(([page, count]) => (
                <div
                  key={page}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100"
                  data-testid={`page-visit-${page}`}
                >
                  <span className="text-sm font-semibold text-blue-700">{count}</span>
                  <span className="text-xs text-blue-600">{page}</span>
                  <span className="text-[10px] text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full">
                    {stats.unique_visit_counts?.[page] || 0} unique
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
